/**
 * ProposeStructureDialog — modal del flujo "Proponer estructura con IA"
 * (Sprint AI-1.g — Sub-fase 5.2).
 *
 * Conecta al StructureAgent del backend para que analice el template +
 * project sources + user hint y proponga las instancias para los
 * repetibles (solo anchors, no contenido detallado).
 *
 * 4 estados internos:
 *
 *   - 'estimate' → carga estimate + textarea hint + botón Proponer
 *   - 'running'  → spinner mientras corre el agente (SSE; puede tomar
 *     minutos). Cerrar o desmontar el modal cancela el agente.
 *   - 'review'   → muestra `proposal` + `rationale` + merge summary;
 *     anchors editables, eliminar instancias, botón Aceptar / Cancelar
 *   - 'error'    → error legible + Reintentar / Cerrar
 *
 * Al aceptar: el caller recibe el `final_proposal` editado (Record<string,
 * unknown>) y se encarga de persistirlo via PUT /content.
 *
 * Anti-alucinación visible: si el agente devuelve arrays vacíos con
 * rationale "no sources retrieved", el modal lo muestra explícito en
 * lugar de aceptar silenciosamente.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  AlertTriangle,
  Layers,
  Loader2,
  Sparkles,
  Trash2,
} from 'lucide-react'

import type {
  AgentMetrics,
  Citation,
  MergeSummary,
  ProposeStructureEstimate,
  ProposeStructureResult,
  WaitingPayload,
} from '../../api/agents'
import {
  estimateProposeStructure,
  proposeStructure,
} from '../../api/agents'
import { ApiError } from '../../api/client'
import { useAccessToken } from '../../auth/useAccessToken'
import {
  apiErrorCode,
  useAiVersionLock,
  useAiVersionLockOn,
} from '../../hooks/useAiVersionLock'
import { formatProfileLabel } from '../../lib/aiLabels'
import { aiWaitingFrom } from '../../lib/aiWaiting'
import { formatDuration } from '../../lib/formatDuration'
import type { TemplateI18nSection } from '../projects/official-viewer/section_types'
import { AiErrorNotice, AiWaitingNotice } from './AiStreamNotices'
import { CitationList } from './CitationList'

interface ProposeStructureDialogProps {
  open: boolean
  onClose: () => void
  artifactId: string
  /** Doc 07 — version target. Cuando el artifact tiene canon activo y
   * el user está editando v(N+1) draft, este id apunta a esa draft. Sin
   * este id el backend cae sobre `current_version_id` (caso v1 inicial). */
  versionId?: string | null
  /** Mapa section_id → i18n section, usado para mostrar títulos
   * legibles del container y del anchor en el panel de revisión. */
  i18nMap: Map<string, TemplateI18nSection>
  /** Callback que recibe el `final_proposal` (con ediciones del user)
   * cuando hace "Aceptar". El caller lo guarda via PUT /content. */
  onAccept: (finalProposal: Record<string, unknown>) => Promise<void> | void
}

type Phase = 'estimate' | 'running' | 'review' | 'error'

export function ProposeStructureDialog(props: ProposeStructureDialogProps) {
  // El contenido se monta al abrir: cada apertura arranca con el estado
  // inicial, y cerrar lo desmonta (lo que corta al agente en curso).
  if (!props.open) return null
  return <ProposeStructureContent {...props} />
}

function ProposeStructureContent({
  onClose,
  artifactId,
  versionId,
  i18nMap,
  onAccept,
}: ProposeStructureDialogProps) {
  const { t } = useTranslation('ai')
  const { accessToken } = useAccessToken()
  const reportVersionLock = useAiVersionLock(artifactId, versionId)

  const [phase, setPhase] = useState<Phase>('estimate')
  const [userHint, setUserHint] = useState('')
  // Estimate fresco en cada apertura: `gcTime: 0` descarta la cache al
  // desmontarse el modal.
  const estimateQuery = useQuery({
    queryKey: ['propose-structure-estimate', artifactId, versionId ?? null],
    queryFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return estimateProposeStructure(accessToken, artifactId, {
        versionId: versionId ?? null,
      })
    },
    enabled: !!accessToken,
    gcTime: 0,
    retry: false,
  })
  // OT-0064 — la estimación ya aplica la regla de editabilidad.
  useAiVersionLockOn(artifactId, versionId, apiErrorCode(estimateQuery.error))
  const estimate = estimateQuery.data ?? null
  const estimateLoading = estimateQuery.isPending
  const estimateError = estimateQuery.error
    ? estimateErrorCode(estimateQuery.error)
    : null

  // Result state — guardamos el `final_proposal` separado para que el
  // user pueda EDITAR los anchors antes de aceptar.
  const [result, setResult] = useState<ProposeStructureResult | null>(null)
  const [editableProposal, setEditableProposal] = useState<
    Record<string, unknown>
  >({})
  const [error, setError] = useState<{ code?: string; message: string } | null>(
    null,
  )
  const [accepting, setAccepting] = useState(false)
  // Request en curso del agente: cerrar el modal o desmontarlo corta la
  // conexión y el backend cancela el agente (OT-0040).
  const proposeAbortRef = useRef<AbortController | null>(null)
  const [waiting, setWaiting] = useState<WaitingPayload | null>(null)

  useEffect(() => () => proposeAbortRef.current?.abort(), [])

  // Esc / backdrop close (bloqueado durante running).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && phase !== 'running' && !accepting) {
        onClose()
      }
    }
    window.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [phase, accepting, onClose])

  async function handlePropose() {
    if (!accessToken) return
    setPhase('running')
    setError(null)
    const ctrl = new AbortController()
    proposeAbortRef.current = ctrl
    try {
      const r = await proposeStructure(
        accessToken,
        artifactId,
        { user_hint: userHint.trim() || null },
        {
          versionId: versionId ?? null,
          signal: ctrl.signal,
          onEvent: (event) => setWaiting(aiWaitingFrom(event)),
        },
      )
      setResult(r)
      // Inicializamos editable con final_proposal — el user edita SOBRE este.
      setEditableProposal(r.final_proposal)
      setPhase('review')
    } catch (err) {
      if (ctrl.signal.aborted) return
      const code = err instanceof ApiError ? err.code : undefined
      // OT-0064 — la versión dejó de ser editable con el diálogo abierto.
      reportVersionLock(code)
      const message =
        err instanceof Error ? err.message : 'unknown error'
      setError({
        ...(code !== undefined ? { code } : {}),
        message,
      })
      setPhase('error')
    } finally {
      if (proposeAbortRef.current === ctrl) proposeAbortRef.current = null
      setWaiting(null)
    }
  }

  /** Detiene al agente (el backend cancela al cerrarse la conexión) y
   * vuelve al paso inicial con el hint intacto. */
  function handleAbort() {
    proposeAbortRef.current?.abort()
    setPhase('estimate')
  }

  async function handleAccept() {
    setAccepting(true)
    try {
      await onAccept(editableProposal)
      onClose()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'save failed'
      setError({ message })
      setPhase('error')
    } finally {
      setAccepting(false)
    }
  }

  const dialogTitle = (() => {
    if (phase === 'running') return t('propose.title_running')
    if (phase === 'review') return t('propose.title_review')
    if (phase === 'error') return t('propose.title_error')
    return t('propose.title_estimate')
  })()

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label={dialogTitle}
      onClick={(e) => {
        if (
          e.target === e.currentTarget &&
          phase !== 'running' &&
          !accepting
        ) {
          onClose()
        }
      }}
    >
      <div className="mx-4 flex max-h-[90vh] w-full max-w-3xl flex-col rounded-md border border-line bg-surface shadow-lg">
        {/* Header */}
        <header className="flex items-center justify-between gap-3 border-b border-line px-5 py-4">
          <div className="flex items-center gap-2">
            <Layers className="h-4 w-4 text-brand" aria-hidden="true" />
            <h2 className="font-display text-lg text-ink">{dialogTitle}</h2>
          </div>
        </header>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {phase === 'estimate' && (
            <EstimateBody
              loading={estimateLoading}
              estimate={estimate}
              estimateError={estimateError}
              userHint={userHint}
              onUserHintChange={setUserHint}
            />
          )}
          {phase === 'running' && <RunningBody waiting={waiting} />}
          {phase === 'review' && result && (
            <ReviewBody
              result={result}
              editableProposal={editableProposal}
              onProposalChange={setEditableProposal}
              i18nMap={i18nMap}
            />
          )}
          {phase === 'error' && error && (
            <AiErrorNotice code={error.code} message={error.message} />
          )}
        </div>

        {/* Footer */}
        <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-line px-5 py-3">
          {phase === 'estimate' && (
            <>
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-line px-3 py-1.5 text-sm text-ink hover:bg-surface-muted"
              >
                {t('dialog.actions.cancel')}
              </button>
              <button
                type="button"
                onClick={handlePropose}
                disabled={
                  estimateLoading ||
                  estimate === null ||
                  estimate.repeatable_count === 0
                }
                className="inline-flex items-center gap-2 rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-white hover:bg-brand/90 disabled:opacity-50"
              >
                <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
                {t('propose.actions.propose')}
              </button>
            </>
          )}
          {phase === 'running' && (
            <button
              type="button"
              onClick={handleAbort}
              className="rounded-md border border-line px-3 py-1.5 text-sm text-ink hover:bg-surface-muted"
            >
              {t('dialog.actions.abort')}
            </button>
          )}
          {phase === 'review' && result && (
            <>
              <button
                type="button"
                onClick={onClose}
                disabled={accepting}
                className="rounded-md border border-line px-3 py-1.5 text-sm text-ink hover:bg-surface-muted disabled:opacity-50"
              >
                {t('dialog.actions.cancel')}
              </button>
              <button
                type="button"
                onClick={handleAccept}
                disabled={accepting || !hasAnythingToAdd(result)}
                className="rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-white hover:bg-brand/90 disabled:opacity-50"
              >
                {accepting
                  ? t('propose.actions.accepting')
                  : t('propose.actions.accept')}
              </button>
            </>
          )}
          {phase === 'error' && (
            <>
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-line px-3 py-1.5 text-sm text-ink hover:bg-surface-muted"
              >
                {t('dialog.actions.close')}
              </button>
              <button
                type="button"
                onClick={() => setPhase('estimate')}
                className="rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-white hover:bg-brand/90"
              >
                {t('dialog.actions.regenerate')}
              </button>
            </>
          )}
        </footer>
      </div>
    </div>
  )
}

// ─── Sub-bodies ─────────────────────────────────────────────────────────────
function EstimateBody({
  loading,
  estimate,
  estimateError,
  userHint,
  onUserHintChange,
}: {
  loading: boolean
  estimate: ProposeStructureEstimate | null
  estimateError: string | null
  userHint: string
  onUserHintChange: (s: string) => void
}) {
  const { t } = useTranslation('ai')
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-ink-subtle py-6">
        <Loader2 className="h-4 w-4 animate-spin" />
        {t('propose.loading_estimate')}
      </div>
    )
  }
  if (estimateError) {
    return (
      <div className="rounded-md border border-critical/40 bg-critical-soft/30 p-3 text-sm text-ink">
        <p className="font-mono text-[10px] uppercase tracking-wider text-ink-subtle">
          {estimateError}
        </p>
        <p>{t('propose.estimate_error')}</p>
      </div>
    )
  }
  if (!estimate) return null
  return (
    <div className="space-y-4 text-sm">
      <p className="text-ink-muted font-body leading-relaxed">
        {t('propose.intro')}
      </p>
      <div className="rounded-md border border-line bg-surface-muted/40 p-3 text-xs space-y-1">
        <div className="flex items-baseline justify-between">
          <span className="text-ink-subtle">
            {t('propose.estimate.repeatables_label')}
          </span>
          <span className="font-medium text-ink">
            {estimate.repeatable_count}
          </span>
        </div>
        <div className="flex items-baseline justify-between">
          <span className="text-ink-subtle">
            {t('propose.estimate.tokens_label')}
          </span>
          <span className="font-mono text-ink">
            ~{formatTokens(estimate.estimated_total_tokens)}
          </span>
        </div>
        <div className="flex items-baseline justify-between">
          <span className="text-ink-subtle">
            {t('dialog.profile_label')}
          </span>
          <span className="text-ink">{formatProfileLabel(estimate.profile_name)}</span>
        </div>
      </div>
      {estimate.repeatable_count === 0 && (
        <div className="rounded-md border border-review/40 bg-review-soft/40 p-3 text-xs text-ink-muted">
          {t('propose.no_repeatables_warning')}
        </div>
      )}
      {estimate.notes.length > 0 && (
        <ul className="rounded-md border border-line bg-surface-muted/40 p-2 text-xs text-ink-muted">
          {estimate.notes.map((n) => (
            <li key={n}>· {n}</li>
          ))}
        </ul>
      )}
      <div className="space-y-1.5">
        <label
          htmlFor="propose-user-hint"
          className="text-xs uppercase tracking-wider text-ink-subtle"
        >
          {t('propose.hint_label')}
        </label>
        <textarea
          id="propose-user-hint"
          value={userHint}
          onChange={(e) => onUserHintChange(e.target.value)}
          placeholder={t('propose.hint_placeholder')}
          rows={3}
          maxLength={500}
          className="w-full resize-y rounded-md border border-line bg-canvas px-3 py-2 text-sm font-body leading-relaxed focus:border-brand focus:outline-none"
        />
        <p className="text-[11px] text-ink-subtle">
          {t('propose.hint_help')}
        </p>
      </div>
      <p className="text-[11px] text-ink-subtle italic">
        {t('propose.review_warning')}
      </p>
    </div>
  )
}

function RunningBody({ waiting }: { waiting: WaitingPayload | null }) {
  const { t } = useTranslation('ai')
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-12 text-sm text-ink-muted">
      <Loader2 className="h-8 w-8 animate-spin text-brand" />
      <p>{t('propose.running_message')}</p>
      <p className="text-[11px] text-ink-subtle italic max-w-md text-center">
        {t('propose.running_hint')}
      </p>
      <AiWaitingNotice waiting={waiting} />
    </div>
  )
}

function ReviewBody({
  result,
  editableProposal,
  onProposalChange,
  i18nMap,
}: {
  result: ProposeStructureResult
  editableProposal: Record<string, unknown>
  onProposalChange: (next: Record<string, unknown>) => void
  i18nMap: Map<string, TemplateI18nSection>
}) {
  const { t } = useTranslation('ai')

  // Resumen del merge — qué se va a agregar y qué se preserva.
  const summary = result.merge_summary
  const totalAdded = useMemo(
    () =>
      Object.values(summary.instances_added).reduce((a, b) => a + b, 0),
    [summary],
  )
  const totalKept = useMemo(
    () =>
      Object.values(summary.instances_kept).reduce((a, b) => a + b, 0),
    [summary],
  )

  return (
    <div className="space-y-4">
      {/* Rationale del LLM */}
      {result.rationale && (
        <div className="rounded-md border border-brand/30 bg-brand-soft/30 p-3 text-sm">
          <p className="text-[10px] uppercase tracking-wider text-ink-subtle mb-1">
            {t('propose.rationale_label')}
          </p>
          <p className="text-ink font-body leading-relaxed">
            {result.rationale}
          </p>
        </div>
      )}

      {/* Stats */}
      <div className="rounded-md border border-line bg-surface-muted/40 p-3 text-xs">
        <p className="text-[10px] uppercase tracking-wider text-ink-subtle mb-1">
          {t('propose.summary_label')}
        </p>
        <p className="text-ink">
          {t('propose.summary_text', {
            added: totalAdded,
            kept: totalKept,
          })}
        </p>
      </div>

      {/* Si el LLM no propuso nada nuevo, lo mostramos explícito */}
      {totalAdded === 0 && (
        <div className="rounded-md border border-review/40 bg-review-soft/40 p-3 text-sm text-ink-muted">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-review shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-ink">
                {t('propose.empty_proposal_title')}
              </p>
              <p className="text-xs mt-1">
                {t('propose.empty_proposal_hint')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Editor de instancias propuestas */}
      <ProposalEditor
        proposal={editableProposal}
        proposed={result.proposal}
        onChange={onProposalChange}
        i18nMap={i18nMap}
      />

      {/* Citations */}
      {result.rag_citations.length > 0 && (
        <CitationList citations={result.rag_citations} />
      )}

      {/* Warnings */}
      {result.warnings.length > 0 && (
        <ul className="rounded-md border border-review/40 bg-review-soft/40 p-2 text-[11px] text-ink-muted">
          {result.warnings.map((w) => (
            <li key={w} className="flex items-start gap-1">
              <AlertTriangle className="h-3 w-3 text-review shrink-0 mt-0.5" />
              <span>{w}</span>
            </li>
          ))}
        </ul>
      )}

      {/* Metrics — discreto */}
      {result.metrics && (
        <p className="text-[10px] text-ink-subtle font-mono">
          {t('propose.metrics_line', {
            tokens: formatTokens(result.metrics.total_tokens),
            duration: formatDuration(result.metrics.total_latency_ms, t),
          })}
        </p>
      )}
    </div>
  )
}

function ProposalEditor({
  proposal,
  proposed,
  onChange,
  i18nMap,
}: {
  proposal: Record<string, unknown>
  proposed: Record<string, unknown>
  onChange: (next: Record<string, unknown>) => void
  i18nMap: Map<string, TemplateI18nSection>
}) {
  const { t } = useTranslation('ai')

  // El final_proposal contiene CURRENT + nuevos. Para el editor mostramos
  // los containers que recibieron items NUEVOS (proposed.keys()) — los
  // existentes no editados se preservan transparentemente.
  const containerIds = Object.keys(proposed).filter((k) => {
    const arr = (proposed as Record<string, unknown>)[k]
    return Array.isArray(arr) && arr.length > 0
  })

  if (containerIds.length === 0) return null

  return (
    <div className="space-y-3">
      <p className="text-[10px] uppercase tracking-wider text-ink-subtle">
        {t('propose.editor_label')}
      </p>
      {containerIds.map((containerId) => (
        <ContainerEditor
          key={containerId}
          containerId={containerId}
          proposal={proposal}
          proposed={proposed}
          onChange={onChange}
          i18nMap={i18nMap}
        />
      ))}
    </div>
  )
}

function ContainerEditor({
  containerId,
  proposal,
  proposed,
  onChange,
  i18nMap,
}: {
  containerId: string
  proposal: Record<string, unknown>
  proposed: Record<string, unknown>
  onChange: (next: Record<string, unknown>) => void
  i18nMap: Map<string, TemplateI18nSection>
}) {
  const { t } = useTranslation('ai')
  const containerTitle = i18nMap.get(containerId)?.title ?? containerId
  const finalArr = (proposal[containerId] as unknown[]) ?? []
  // Cuántas instancias del proposal eran NUEVAS (vs preservadas del current).
  // El proposed[container] solo trae las nuevas; el final_proposal trae las
  // existentes primero y luego las nuevas (orden del merge aditivo).
  const proposedCount = Array.isArray(proposed[containerId])
    ? (proposed[containerId] as unknown[]).length
    : 0
  const existingCount = finalArr.length - proposedCount

  // Heurística: el primer campo string del primer instance es el anchor.
  const anchorKey = useMemo(() => {
    if (proposedCount === 0) return null
    const first = (proposed[containerId] as unknown[])[0]
    if (!first || typeof first !== 'object') return null
    for (const [k, v] of Object.entries(first)) {
      if (typeof v === 'string') return k
    }
    return null
  }, [proposed, containerId, proposedCount])

  function updateAnchor(idx: number, value: string) {
    if (!anchorKey) return
    const next = { ...proposal }
    const arr = [...((next[containerId] as unknown[]) ?? [])]
    const item = arr[idx]
    if (item && typeof item === 'object') {
      arr[idx] = { ...item, [anchorKey]: value }
    }
    next[containerId] = arr
    onChange(next)
  }

  function removeInstance(idx: number) {
    const next = { ...proposal }
    const arr = [...((next[containerId] as unknown[]) ?? [])]
    arr.splice(idx, 1)
    next[containerId] = arr
    onChange(next)
  }

  return (
    <div className="rounded-md border border-line bg-canvas">
      <header className="flex items-center justify-between gap-2 border-b border-line px-3 py-2">
        <div className="flex items-center gap-2">
          <Layers className="h-3.5 w-3.5 text-brand" />
          <span className="text-sm font-medium text-ink">
            {containerTitle}
          </span>
        </div>
        <span className="text-[10px] text-ink-subtle">
          {existingCount > 0
            ? t('propose.container_count_with_existing', {
                added: proposedCount,
                kept: existingCount,
              })
            : t('propose.container_count', { added: proposedCount })}
        </span>
      </header>
      <ul className="divide-y divide-line">
        {finalArr.map((item, idx) => {
          const isNew = idx >= existingCount
          const anchorValue =
            anchorKey && item && typeof item === 'object'
              ? String(
                  (item as Record<string, unknown>)[anchorKey] ?? '',
                )
              : ''
          return (
            <li
              key={`${containerId}-${idx}`}
              className={`flex items-center gap-2 px-3 py-2 ${
                isNew ? 'bg-brand-soft/20' : 'opacity-60'
              }`}
            >
              <span
                className={`text-[10px] uppercase tracking-wider px-1 py-0.5 rounded shrink-0 ${
                  isNew
                    ? 'bg-brand/15 text-brand'
                    : 'bg-line text-ink-subtle'
                }`}
              >
                {isNew ? t('propose.tag_new') : t('propose.tag_existing')}
              </span>
              {isNew && anchorKey ? (
                <input
                  type="text"
                  value={anchorValue}
                  onChange={(e) => updateAnchor(idx, e.target.value)}
                  className="flex-1 rounded border border-line bg-canvas px-2 py-1 text-sm font-body focus:border-brand focus:outline-none"
                />
              ) : (
                <span className="flex-1 text-sm text-ink-muted truncate">
                  {anchorValue || `(${t('propose.no_anchor_value')})`}
                </span>
              )}
              {isNew && (
                <button
                  type="button"
                  onClick={() => removeInstance(idx)}
                  aria-label={t('propose.remove_instance')}
                  className="text-ink-subtle hover:text-critical"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

// ─── Helpers ────────────────────────────────────────────────────────────────
function estimateErrorCode(err: Error): string {
  return err instanceof ApiError ? err.code : err.message
}

function hasAnythingToAdd(result: ProposeStructureResult): boolean {
  /** True si el final_proposal difiere del current (o sea, hay algo
   * que vale la pena guardar). Heurística simple: alguna container del
   * proposal tiene items. */
  for (const v of Object.values(result.proposal)) {
    if (Array.isArray(v) && v.length > 0) return true
  }
  return false
}

function formatTokens(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`
  return String(n)
}

// Re-export Citation type para los consumers.
export type {
  AgentMetrics,
  Citation,
  MergeSummary,
  ProposeStructureResult,
}
